import React from 'react';
import styled from '@emotion/styled/macro';
import vars from '@variables';
import { AAtom } from '@atoms/link-atoms/link-atoms';
import { defineResponsiveGridColumns } from '@utils/utils';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { LegendProps } from './legend-block.types';
import { PAtom } from '@atoms/text-atoms/text-atoms';
import { StandardImageAtom } from '@atoms/image-atoms/image-atoms';

const Legend: React.FC<LegendProps> = ({
  logoSrc,
  companyName,
  origin,
  founded,
  website,
  className,
}) => {
  if (companyName || origin || founded || website) {
    return (
      <div className={className}>
        <div className="legend-wrapper" data-testid="legend-block">
          {logoSrc && (
            <div className="legend-logo">
              <StandardImageAtom
                src={logoSrc}
                alt={`${companyName} logo`}
                data-testid="legend-logo"
              />
            </div>
          )}
          <div className="legend-section">
            {companyName && (
              <div className="legend-text">
                <PAtom>name:</PAtom>
                <PAtom>{companyName}</PAtom>
              </div>
            )}
            {founded && (
              <div className="legend-text">
                <PAtom>founded:</PAtom>
                <PAtom>{founded}</PAtom>
              </div>
            )}
            {origin && (
              <div className="legend-text">
                <PAtom>origin:</PAtom>
                <PAtom>{origin}</PAtom>
              </div>
            )}
            {website && (
              <div className="legend-line">
                <AAtom
                  href={website}
                  target="_blank"
                  data-testid="legend-website"
                  data-gtm-id={GTM_ID.LEGEND_BLOCK_LINK}
                >
                  View website
                </AAtom>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const StyledLegend = styled(Legend)`
  --logo-width: 18.4rem;
  --logo-height: 12.8rem;

  width: 100%;
  display: grid;
  grid-template-columns: ${defineResponsiveGridColumns(4)};
  grid-column: col1-start / col4-end;
  column-gap: ${vars.mobileColumnPercentageGap};

  .legend-wrapper {
    display: flex;
    grid-column: col1-start / col4-end;
    flex-direction: row;
    border-bottom: 1px solid ${vars.borderGrey};
    padding-bottom: ${vars.spacingM};
    margin-bottom: ${vars.spacingM};

    .legend-logo {
      grid-column: col1-start / col4-end;
      display: flex;
      width: var(--logo-width);
      height: var(--logo-height);
      padding-bottom: ${vars.spacingS};
      ${StandardImageAtom} {
        object-fit: contain;
        margin: inherit;
        width: 100%;
      }
    }

    .legend-section {
      min-width: 22.1rem;
      justify-content: center;

      ${PAtom} {
        font-weight: ${vars.fontWeightRegular};
        ${vars.fontSize2}
        margin: 0 ${vars.spacingXs} 0 0;
        &:first-letter {
          text-transform: uppercase;
        }
        &:first-of-type {
          font-weight: ${vars.fontWeightBold};
        }
      }

      ${AAtom} {
        ${vars.fontSize2}
        font-weight: ${vars.fontWeightRegular};
        margin: 0 ${vars.spacingXs} 0 0;
      }

      .legend-text {
        display: flex;
        justify-content: start;
        &:not(:last-of-type) {
          padding-bottom: ${vars.spacingS};
        }
      }
    }
  }

  @media screen and (min-width: ${vars.breakPointTablet}) {
    grid-column: col1-start / col10-end;
    column-gap: ${vars.standardColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(8)};
    margin-top: ${vars.spacingL};

    .legend-wrapper {
      grid-column: col2-start / col7-end;
      flex-direction: row;

      .legend-logo {
        padding-bottom: 0;

        ${StandardImageAtom} {
          padding-bottom: 1px;
        }
      }
    }

    .legend-section {
      grid-column: col2-start / col7-end;
      display: flex;
      flex-direction: column;
      margin-top: 0;
      text-align: left;
      &:nth-of-type(2) {
        margin-left: ${vars.spacingL};
      }
    }
  }

  @media only screen and (min-width: ${vars.breakPointSmallDesktop}) {
    grid-template-columns: ${defineResponsiveGridColumns(12)};
    grid-column: col3-start / col10-end;

    .legend-wrapper, .legend-section {
      grid-column: col3-start / col10-end;
    }

`;

export default StyledLegend;
